/* Use the actual product-page section, preserving its native section/form IDs.
   Variant requests therefore resolve against the product template, not the page.
   Prices, gallery filtering, availability and bundle rules keep their owners. */
(() => {
  function showPairInRepeatedGallerySlot(root, embedded) {
    if (!root.dataset.pairImageUrl) return;
    const gallery = embedded.querySelector('product-gallery');
    const mediaImages = [...(gallery?.querySelectorAll('.product-gallery__media-list .product-gallery__media > img') || [])];
    const seen = new Set();
    const duplicateIndex = mediaImages.findIndex(image => {
      const key = new URL(image.getAttribute('src'), location.origin).pathname;
      if (seen.has(key)) return true;
      seen.add(key);
      return false;
    });
    if (duplicateIndex < 0) return;
    const thumbnails = [...gallery.querySelectorAll('.product-gallery__thumbnail-list .product-gallery__thumbnail img')];
    [mediaImages[duplicateIndex], thumbnails[duplicateIndex]].filter(Boolean).forEach(image => {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = root.dataset.pairImageUrl;
      image.alt = root.dataset.pairImageAlt || 'Selected second product';
    });
    root.dataset.galleryPair = 'ready';
  }

  // Seed the advertised pair through Kaching's own chooser, once per page.
  // Its native state continues to own variants, prices, Swap and cart submission.
  function seedDefaultPair(root) {
    const handle = root.dataset.defaultPairHandle || (root.dataset.productId === '10567256539317' ? 'infinity-butterfly-necklace' : '');
    if (!handle) return;
    const primaryHandle = new URL(root.dataset.productUrl, location.origin).pathname.split('/').filter(Boolean).pop();
    let stage = 'waiting';
    let timer;
    let poll;
    let modal;
    const style = document.createElement('style');
    style.textContent = 'html[data-bfe-seeding-pair] .kaching-bundles__choose-product-modal { visibility: hidden; }';
    const finish = status => {
      observer.disconnect();
      clearTimeout(timer);
      clearInterval(poll);
      root.removeEventListener('pointerdown', onInteraction, true);
      root.removeEventListener('keydown', onInteraction, true);
      document.documentElement.removeAttribute('data-bfe-seeding-pair');
      style.remove();
      root.dataset.defaultPair = status;
    };
    const onInteraction = event => {
      if (event.isTrusted && (stage === 'waiting' || stage === 'select-pair') && event.target.closest('kaching-bundles-block [role="button"], kaching-bundles-block select, variant-picker, buy-buttons')) finish('customer-selected');
    };
    const step = () => {
      const widget = root.querySelector('kaching-bundles-block');
      const pair = widget?.dealBlock?.dealBars?.find(deal => deal.dealBarType === 'quantity-break' && Number(deal.quantity) === 2);
      if (!pair) return;
      const bar = [...widget.querySelectorAll('[data-deal-bar-id]')].find(item => item.dataset.dealBarId === pair.id);
      if (!bar) return;
      if (!bar.classList.contains('kaching-bundles__bar--selected')) {
        if (stage !== 'waiting') return;
        const selectPair = bar.querySelector('[role="button"]');
        if (!selectPair) return;
        stage = 'select-pair';
        selectPair.click();
        return;
      }
      if (stage === 'select-pair') stage = 'waiting';
      if (stage === 'waiting') {
        const product = widget.collectionBreaksProducts?.find(item => item.handle === handle || item.url?.split('?')[0].endsWith('/' + handle));
        if (!product?.variants?.some(variant => variant.availableForSale)) return;
        const second = bar.querySelector('.kaching-bundles__collection-product__remove-button')?.closest('.kaching-bundles__collection-product');
        if (second?.querySelector('a[href*="/products/' + handle + '"]')) return finish('ready');
        const remove = second?.querySelector('.kaching-bundles__collection-product__remove-button');
        if (!remove) return;
        // Only replace the app's duplicated primary product, never a customer's selection.
        if (!second.querySelector('a[href*="/products/' + primaryHandle + '"]')) return finish('customer-selected');
        stage = 'choose';
        document.head.append(style);
        document.documentElement.setAttribute('data-bfe-seeding-pair', '');
        remove.click();
      } else if (stage === 'choose') {
        const choose = bar.querySelector('.kaching-bundles__collection-product__choose-product-button');
        if (choose) { stage = 'modal'; choose.click(); }
      } else if (stage === 'modal') {
        modal = document.querySelector('.kaching-bundles__choose-product-modal');
        const link = modal?.querySelector('a[href*="/products/' + handle + '"]');
        const choose = link?.closest('.kaching-bundles__choose-product__product')?.querySelector('.kaching-bundles__choose-product__product-button');
        if (choose && choose.getAttribute('aria-disabled') !== 'true') { stage = 'verify'; choose.click(); }
      } else if (stage === 'verify' && bar.querySelector('.kaching-bundles__collection-product a[href*="/products/' + handle + '"]')) {
        finish('ready');
      }
    };
    const observer = new MutationObserver(step);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['collection-breaks-products'] });
    root.addEventListener('pointerdown', onInteraction, true);
    root.addEventListener('keydown', onInteraction, true);
    timer = setTimeout(() => {
      modal?.querySelector('.kaching-bundles__choose-product__close')?.click();
      finish('unavailable');
    }, 60000);
    // The app may supply products by a property update after its DOM settles.
    poll = setInterval(step, 100);
    step();
  }

  document.querySelectorAll('[data-bfe-ad-product]').forEach(async (root) => {
    if (root.dataset.loading) return;
    root.dataset.loading = 'true';
    const mount = root.querySelector('[data-product-mount]');
    try {
      const url = new URL(root.dataset.productUrl, location.origin);
      // Render the approved product-card treatment without changing the
      // visitor's page URL or the ordinary product template.
      url.searchParams.set('view', 'atc-demo');
      const current = new URL(location.href);
      ['country', 'currency', 'preview_theme_id'].forEach(key => {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      });
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Product section unavailable');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = page.querySelector('.shopify-section--main-product');
      const purchase = source?.querySelector('product-rerender[allow-partial-rerender]');
      if (!purchase?.querySelector('form[is="product-form"]')) throw new Error('Product form unavailable');
      const embedded = document.createElement('section');
      embedded.id = source.id;
      embedded.className = source.className;
      for (const style of source.querySelectorAll(':scope > style')) embedded.append(style.cloneNode(true));
      const container = purchase.parentElement.cloneNode(false);
      container.append(purchase.cloneNode(true));
      embedded.append(container);
      // Keep the article's own H1 and URL. Only the native purchase section is embedded.
      embedded.querySelectorAll('h1').forEach(heading => {
        const replacement = document.createElement('h2');
        for (const attribute of heading.attributes) replacement.setAttribute(attribute.name, attribute.value);
        replacement.innerHTML = heading.innerHTML;
        heading.replaceWith(replacement);
      });
      embedded.querySelectorAll('variant-picker').forEach(picker => picker.removeAttribute('update-url'));
      embedded.querySelectorAll('script:not([type="application/json"])').forEach(script => script.remove());
      showPairInRepeatedGallerySlot(root, embedded);
      mount.replaceChildren(embedded);
      seedDefaultPair(root);
      await Promise.all(['variant-picker', 'product-form', 'product-gallery'].map(name => customElements.whenDefined(name)));
      const form = embedded.querySelector('form[is="product-form"]');
      const buyBlock = embedded.querySelector('.product-info__block-item[data-block-type="buy-buttons"]');
      if (!embedded.querySelector('kaching-bundle')) {
        const widget = document.createElement('kaching-bundle');
        widget.setAttribute('product-id', root.dataset.productId);
        buyBlock.prepend(widget);
      }
      // The app exposes this initializer for custom placements. Its normal
      // DOM-ready pass happens before our product section has arrived.
      for (let attempt = 0; attempt < 100 && !window.__kachingBundlesInitializeInternal; attempt++) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      if (window.__kachingBundlesInitializeInternal) window.__kachingBundlesInitializeInternal();
      // Inline product-page scripts are intentionally not executed on insertion.
      // Preserve the material bullet's update through the native variant event.
      form.addEventListener('variant:change', event => {
        embedded.querySelectorAll('variant-picker').forEach(picker => picker.removeAttribute('update-url'));
        const point = embedded.querySelector('[data-bfe-material-point]');
        const variant = event.detail?.variant;
        if (!point || !variant) return;
        const finish = String(variant.options[Number(point.dataset.bfeMaterialPosition || 1) - 1] || '').toLowerCase();
        if (finish.includes('gold')) point.textContent = point.dataset.bfeGoldCopy;
        if (finish.includes('silver')) point.textContent = point.dataset.bfeSilverCopy;
      });
      if (!document.querySelector('script[data-bfe-ad-meals]')) {
        const script = document.createElement('script');
        script.src = root.dataset.mealsScript;
        script.dataset.bfeAdMeals = '';
        document.body.append(script);
      }
      root.dataset.ready = 'true';
    } catch (error) {
      root.dataset.error = 'true';
      console.warn('Advertorial product: the product-page link remains available.', error);
    }
  });
})();
